/**
 * Reports (BUILD_PLAN.md section 13; ADR 0123): a report to choose, the
 * filters every report shares, and the chosen report under them. Each report
 * is a route under /reports, listed in REPORTS; choosing another keeps the
 * filters, which live in the URL. A report that's as of now (unsubmitted
 * aging, open tickets) has no days: its filters are the techs and clients,
 * and the Filters panel says what it shows instead. A tech's
 * reports are of their own work (section 6.2), so they have no Techs filter;
 * an owner's are of anyone's. The API decides who may see what, and checks
 * the filters again.
 */
import { useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { matchPath, NavLink, Outlet, useLocation } from "react-router";

import type { ClientChoice, ReportFilters, UserSummary } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Combobox, type ComboboxOption } from "../../components/ui/Combobox";
import { Panel } from "../../components/ui/Panel";
import { useReportFilters } from "./filters";

/**
 * The reports, in the chooser's order. `dated`: it covers the filters' days;
 * if not, it's as of now, and `asOf` says what it shows instead.
 */
const REPORTS: readonly { label: string; path: string; end: boolean; dated: boolean; asOf?: string }[] = [
  { label: "Tech activity", path: "/reports", end: true, dated: true },
  { label: "Utilization", path: "/reports/utilization", end: false, dated: true },
  { label: "Managed client effort", path: "/reports/managed-effort", end: false, dated: true },
  {
    label: "Unsubmitted aging",
    path: "/reports/unsubmitted-aging",
    end: false,
    dated: false,
    asOf: "As of now, so no days: every block still to log, whatever its day, aged from each tech's own today.",
  },
  {
    label: "Open tickets",
    path: "/reports/open-tickets",
    end: false,
    dated: false,
    asOf:
      "As of now, so no days: each tech's tickets from every day, assigned to them or with their time, aged " +
      "from each tech's own today.",
  },
  { label: "Client activity audit", path: "/reports/client-audit", end: false, dated: true },
];

const inputClass =
  "h-10 rounded-input border border-line bg-card px-3 text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand";

function userName(user: UserSummary): string {
  return user.display_name ?? user.email ?? "Unnamed user";
}

function clientLabel(client: ClientChoice): string {
  return client.company_type_label === "Customer" ? client.name : `${client.name} (${client.company_type_label})`;
}

/**
 * Several values in one field: the Combobox adds one at a time, and each
 * chosen value is a chip with × to remove it. None chosen means all.
 */
function MultiPicker({
  label,
  options,
  chosen,
  onChange,
  all,
  loading,
  failed,
  failedText,
}: {
  label: string;
  options: ComboboxOption[];
  chosen: readonly string[];
  onChange: (values: string[]) => void;
  /** What none chosen means, e.g. "Every tech". */
  all: string;
  loading: boolean;
  failed: boolean;
  failedText: string;
}) {
  const labels = new Map(options.map((o) => [o.value, o.label]));
  return (
    <div className="flex w-72 max-w-full flex-col gap-1.5">
      <Combobox
        label={label}
        options={options.filter((o) => !chosen.includes(o.value))}
        value=""
        onChange={(value) => {
          if (value !== "") {
            onChange([...chosen, value]);
          }
        }}
        loading={loading}
        failed={failed}
        failedText={failedText}
        placeholder="Add"
      />
      <ul aria-label={`${label} chosen`} className="flex flex-wrap gap-1.5">
        {chosen.length === 0 ? (
          <li className="text-[13px] text-muted">{all}</li>
        ) : (
          chosen.map((value) => {
            const name = labels.get(value) ?? "Unknown";
            return (
              <li
                key={value}
                className="inline-flex max-w-full items-center gap-1 rounded-input bg-brand-tint py-0.5 pr-1 pl-2.5 text-sm text-brand-ink-on-tint"
              >
                <span className="min-w-0 break-words">{name}</span>
                <button
                  type="button"
                  aria-label={`Remove ${name}`}
                  className="shrink-0 cursor-pointer rounded-pill px-1 hover:bg-card"
                  onClick={() => {
                    onChange(chosen.filter((v) => v !== value));
                  }}
                >
                  ×
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}

/**
 * The filters every report takes: the days, the techs and the clients. A
 * report that's as of now has no days, and says so; they stay in the URL
 * for the next report.
 */
function Filters({
  filters,
  onChange,
  dated,
  asOf,
}: {
  filters: ReportFilters;
  onChange: (next: ReportFilters) => void;
  dated: boolean;
  /** What a report as of now shows, in place of the days. */
  asOf: string | undefined;
}) {
  const { api, me } = useSession();
  const isOwner = me.role === "owner";
  const headingId = useId();
  const startId = useId();
  const endId = useId();
  // The users list is the owner's; a tech's reports are of themselves.
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
    staleTime: 5 * 60_000,
    retry: 1,
    enabled: isOwner,
  });
  const clients = useQuery({
    queryKey: ["client-choices"],
    queryFn: () => api.listClientChoices(),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const techOptions: ComboboxOption[] = (users.data ?? []).map((user) => ({
    value: user.id,
    label: userName(user),
    detail: [user.id === me.id ? "you" : null, user.is_active ? null : "inactive"].filter(Boolean).join(", ") || undefined,
  }));
  const clientOptions: ComboboxOption[] = (clients.data ?? []).map((client) => ({
    value: client.id,
    label: clientLabel(client),
  }));

  return (
    <Panel aria-labelledby={headingId} className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 id={headingId} className="text-lg font-semibold tracking-tight">
          Filters
        </h2>
        <p className="text-[13.5px] text-muted">
          {dated ? "Days are each tech's own, in their timezone; up to 92 at a time." : (asOf ?? "As of now, so no days.")}
        </p>
      </div>
      <div className="flex flex-wrap items-start gap-4">
        {dated ? (
          <>
            <div className="flex flex-col gap-[5px]">
              <label htmlFor={startId} className="text-xs font-semibold text-muted">
                From
              </label>
              <input
                id={startId}
                type="date"
                required
                className={inputClass}
                value={filters.start}
                onChange={(event) => {
                  if (event.target.value !== "") {
                    onChange({ ...filters, start: event.target.value });
                  }
                }}
              />
            </div>
            <div className="flex flex-col gap-[5px]">
              <label htmlFor={endId} className="text-xs font-semibold text-muted">
                To
              </label>
              <input
                id={endId}
                type="date"
                required
                className={inputClass}
                value={filters.end}
                onChange={(event) => {
                  if (event.target.value !== "") {
                    onChange({ ...filters, end: event.target.value });
                  }
                }}
              />
            </div>
          </>
        ) : null}
        {isOwner ? (
          <MultiPicker
            label="Techs"
            options={techOptions}
            chosen={filters.userIds}
            onChange={(userIds) => {
              onChange({ ...filters, userIds });
            }}
            all="Every tech"
            loading={users.isPending}
            failed={users.isError}
            failedText="Couldn't load the techs"
          />
        ) : null}
        <MultiPicker
          label="Clients"
          options={clientOptions}
          chosen={filters.clientIds}
          onChange={(clientIds) => {
            onChange({ ...filters, clientIds });
          }}
          all="Every client"
          loading={clients.isPending}
          failed={clients.isError}
          failedText="Couldn't load clients"
        />
      </div>
    </Panel>
  );
}

export function ReportsPage() {
  const location = useLocation();
  const [filters, setFilters] = useReportFilters();
  const current = REPORTS.find((report) => matchPath({ path: report.path, end: report.end }, location.pathname));
  return (
    <>
      <Panel className="flex flex-wrap items-center gap-6">
        <h1 className="text-xl font-semibold tracking-tight">Reports</h1>
        <nav aria-label="Reports" className="flex gap-1">
          {REPORTS.map((report) => (
            <NavLink
              key={report.path}
              // The filters come along.
              to={{ pathname: report.path, search: location.search }}
              end={report.end}
              className={({ isActive }) =>
                "flex h-[38px] items-center rounded-pill px-4 text-sm " +
                (isActive
                  ? "bg-brand-tint font-semibold text-brand-ink-on-tint"
                  : "font-medium text-muted hover:text-text")
              }
            >
              {report.label}
            </NavLink>
          ))}
        </nav>
      </Panel>
      <Filters filters={filters} onChange={setFilters} dated={current?.dated ?? true} asOf={current?.asOf} />
      <Outlet />
    </>
  );
}
